import { PostgrestError, PostgrestSingleResponse, SupabaseClient } from '@supabase/supabase-js';

import { UpsertMediaManagerRenderedImage } from '../../domain/interfaces/supabase/media-manager/actions';
import { MediaManagerRenderedImageDto } from '../../domain/interfaces/supabase/media-manager/dto';
import { MediaManagerRenderedImage } from '../../domain/interfaces/supabase/media-manager/entity';
import mediaManagerRenderedImageDataMapper from '../../domain/mappers/media-manager-rendered-image-data-mapper';
import {
  RenderImageError,
  RenderImageErrorCode,
} from 'modules/manager/domain/render/errors/render-image-errors';

const RENDERED_IMAGES_TABLE = 'media_manager_rendered_images';

export default class MediaManagerRenderedImages {
  constructor(private readonly supabase: SupabaseClient) {}

  async upsert(input: UpsertMediaManagerRenderedImage): Promise<MediaManagerRenderedImage> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .upsert(
        {
          match_id: input.matchId,
          board_id: input.boardId,
          image_url: input.imageUrl,
        },
        { onConflict: 'match_id,board_id' },
      )
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerRenderedImageDto>;

    if (error) {
      throw classifyPostgrestError(error, 'upsert', {
        matchId: input.matchId,
        boardId: input.boardId,
      });
    }

    return mediaManagerRenderedImageDataMapper(data);
  }

  async getById(id: string): Promise<MediaManagerRenderedImage | null> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .select('*')
      .eq('id', id)
      .maybeSingle()) as PostgrestSingleResponse<MediaManagerRenderedImageDto | null>;

    if (error) {
      throw classifyPostgrestError(error, 'select', { id });
    }

    return data ? mediaManagerRenderedImageDataMapper(data) : null;
  }

  async getByMatchAndBoard(
    matchId: number,
    boardId: number,
  ): Promise<MediaManagerRenderedImage | null> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .select('*')
      .eq('match_id', matchId)
      .eq('board_id', boardId)
      .maybeSingle()) as PostgrestSingleResponse<MediaManagerRenderedImageDto | null>;

    if (error) {
      throw classifyPostgrestError(error, 'select', { matchId, boardId });
    }

    return data ? mediaManagerRenderedImageDataMapper(data) : null;
  }

  async deleteById(id: string): Promise<void> {
    const { error } = await this.supabase.from(RENDERED_IMAGES_TABLE).delete().eq('id', id);

    if (error) {
      throw classifyPostgrestError(error, 'delete', { id });
    }
  }
}

const classifyPostgrestError = (
  err: PostgrestError,
  op: 'upsert' | 'select' | 'delete',
  ctx: Record<string, unknown>,
): RenderImageError => {
  const ctxStr = Object.entries(ctx)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(' ');

  if (err.code === '23505') {
    return new RenderImageError(
      RenderImageErrorCode.DUPLICATE_IMAGE,
      `[supabase] unique violation during ${op} (${ctxStr}): ${err.message}`,
      'An image for this match and board already exists.',
      err,
    );
  }
  if (err.code === '23503') {
    return new RenderImageError(
      RenderImageErrorCode.INVALID_PAYLOAD,
      `[supabase] FK violation during ${op} (${ctxStr}): ${err.message}`,
      'The referenced board no longer exists.',
      err,
    );
  }
  if (op === 'delete') {
    return new RenderImageError(
      RenderImageErrorCode.DELETION_FAILED,
      `[supabase] delete failed (${ctxStr}): ${err.message}`,
      'Failed to remove the image record. Please try again.',
      err,
    );
  }
  return new RenderImageError(
    RenderImageErrorCode.DB_INSERT_FAILED,
    `[supabase] ${op} failed (${ctxStr}) code=${err.code}: ${err.message}`,
    'Failed to record the image. Please try again.',
    err,
  );
};
